import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["help"]

  static values = {
    enabled: { type: Boolean, default: true }
  }

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  handleKeydown(event) {
    if (!this.enabledValue) return

    // Ignore if user is typing in an input
    if (this.isInputFocused()) return

    // Ignore if modifier keys are pressed (except for combos we want)
    if (event.metaKey || event.ctrlKey || event.altKey) return

    switch (event.key.toLowerCase()) {
      case "r":
        event.preventDefault()
        this.triggerRefresh()
        break

      case "1":
        event.preventDefault()
        this.selectEnvironment("production")
        break

      case "2":
        event.preventDefault()
        this.selectEnvironment("staging")
        break

      case "3":
        event.preventDefault()
        this.selectEnvironment("development")
        break

      case "f":
        event.preventDefault()
        this.focusStatusFilter()
        break

      case "?":
        event.preventDefault()
        this.toggleHelp()
        break

      case "escape":
        if (this.hasHelpTarget && !this.helpTarget.classList.contains("hidden")) {
          event.preventDefault()
          this.hideHelp()
        }
        break
    }
  }

  isInputFocused() {
    const active = document.activeElement
    const inputTags = ["INPUT", "TEXTAREA", "SELECT"]
    return inputTags.includes(active?.tagName) || active?.isContentEditable
  }

  triggerRefresh() {
    // Find and trigger the refresh button or dispatch event
    const refreshButton = document.querySelector("[data-action*='refresh']")
    if (refreshButton) {
      refreshButton.click()
    } else {
      // Dispatch a custom event that other controllers can listen for
      this.dispatch("refresh")
    }

    this.showToast("Refreshing...")
  }

  selectEnvironment(env) {
    // Find the environment filter controller and trigger selection
    const envButton = document.querySelector(`[data-environment="${env}"]`)
    if (envButton) {
      envButton.click()
    }
  }

  focusStatusFilter() {
    const filter = document.querySelector("[data-status-filter]")
    if (filter) {
      filter.focus()
    }
  }

  toggleHelp() {
    if (this.hasHelpTarget) {
      this.helpTarget.classList.toggle("hidden")
    } else {
      this.showHelpModal()
    }
  }

  hideHelp() {
    if (this.hasHelpTarget) {
      this.helpTarget.classList.add("hidden")
    }
  }

  showHelpModal() {
    // Create a temporary help overlay
    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    overlay.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Keyboard Shortcuts</h3>
        <dl class="space-y-2 text-sm">
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">R</dt>
            <dd class="text-gray-600">Refresh dashboard</dd>
          </div>
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">1</dt>
            <dd class="text-gray-600">Switch to Production</dd>
          </div>
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">2</dt>
            <dd class="text-gray-600">Switch to Staging</dd>
          </div>
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">3</dt>
            <dd class="text-gray-600">Switch to Development</dd>
          </div>
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">F</dt>
            <dd class="text-gray-600">Focus status filter</dd>
          </div>
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">?</dt>
            <dd class="text-gray-600">Show this help</dd>
          </div>
          <div class="flex justify-between">
            <dt class="font-mono bg-gray-100 px-2 py-0.5 rounded">Esc</dt>
            <dd class="text-gray-600">Close dialogs</dd>
          </div>
        </dl>
        <button class="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 rounded-md text-sm font-medium">
          Close
        </button>
      </div>
    `

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay || e.target.tagName === "BUTTON") {
        overlay.remove()
      }
    })

    document.body.appendChild(overlay)
  }

  showToast(message) {
    const toast = document.createElement("div")
    toast.className = "fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm z-50 transition-opacity duration-300"
    toast.textContent = message

    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add("opacity-0")
      setTimeout(() => toast.remove(), 300)
    }, 2000)
  }
}
